import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { useDashboard } from "@/contexts/DashboardContext";
import { StatusOverTimeRow } from "@/lib/axios/types/widgets.type";
import { formatSeconds } from "@/lib/utils";
import { calculateCustomDateRange } from "@/utils/dateRangeUtils";
import { fetchStatusOverTimePaged } from "@/lib/axios/api/widgets.api";
import { PaginatedResponse } from "@/lib/axios/types";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import React from "react";
import AgentBarRow from "../common/AgentBarRow";

const STATUS_GROUPS = {
  'Available': ['available'],
  'In a call': ['inACall'],
  // 'In a call': ['inACall', 'inAConferenceCall'],
  'Not available': ['away', 'beRightBack', 'offline', 'presenceUnknown', 'outOfOffice', 'offWork', 'inactive'],
  'Busy': ['busy', 'inAMeeting', 'doNotDisturb', 'presenting', 'focusing', 'inAConferenceCall'],
  // 'Busy': ['busy', 'inAMeeting', 'doNotDisturb', 'presenting', 'focusing', 'urgentInterruptionsOnly']
  'noData': ['noData']
};

// Generate colors for grouped status categories
const generateStatusColors = (theme: string) => {
  return theme === 'dark' ? {
    // Dark theme colors (lighter/softer colors)
    'Available': '#A5D6A7',
    'In a call': '#90CAF9',
    'Not available': '#B0BEC5',
    'Busy': '#FFAB91',
    'noData': '#9CA3AF'
  } : {
    // Light theme colors (darker/bolder colors)
    'Available': '#26C872',
    'In a call': '#2196F3',
    'Not available': '#616161',
    'Busy': '#cc290c',
    'noData': '#9CA3AF'
  };
};

// Natural sort for agent names with numbers
function naturalAgentSort(a: { agent: string }, b: { agent: string }) {
  const getParts = (str: string): [string, number] => {
    const match = str.match(/^(.*\D)?(\d+)$/);
    if (match) return [match[1] || '', Number(match[2])];
    return [str, NaN];
  };
  const [aPrefix, aNum] = getParts(a.agent);
  const [bPrefix, bNum] = getParts(b.agent);
  if (aPrefix === bPrefix && !isNaN(aNum) && !isNaN(bNum)) {
    return aNum - bNum;
  }
  return a.agent.localeCompare(b.agent, undefined, { sensitivity: 'base' });
}

// Format status name for display - now handles grouped categories
const formatStatusName = (statusKey: string): string => {
  // Handle special case for noData
  if (statusKey === 'noData') {
    return 'No call records were found';
  }
  // Return the grouped category name directly
  return statusKey;
};

interface StatusOverTimeWidgetProps {
  startDate: string;
  endDate: string;
  previousStartDate?: string;
  previousEndDate?: string;
  queueMicrosoftIds?: string[] | null;
  agentMicrosoftIds?: string[] | null;
  groupIds?: number[] | null;
  unifiedChartHeight?: number;
  agentLimit?: number;
}

const StatusOverTimeWidget: React.FC<StatusOverTimeWidgetProps> = ({
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  queueMicrosoftIds,
  agentMicrosoftIds,
  groupIds,
  unifiedChartHeight,
  agentLimit = 20
}) => {
  const { theme } = useDashboard();
  const [page, setPage] = React.useState<number>(1);
  const [data, setData] = React.useState<PaginatedResponse<StatusOverTimeRow> | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  // Constants
  const limit = 10;
  const MAX_ROWS_FOR_CARD = 10;

  // Fetch data effect
  React.useEffect(() => {
    const controller = new AbortController();
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(undefined);

    const prev = previousStartDate && previousEndDate
      ? { previousFrom: new Date(previousStartDate), previousTo: new Date(previousEndDate) }
      : calculateCustomDateRange(new Date(startDate), new Date(endDate));

    fetchStatusOverTimePaged({
      startDate,
      endDate,
      previousStartDate: prev.previousFrom?.toISOString(),
      previousEndDate: prev.previousTo?.toISOString(),
      queueMicrosoftIds: queueMicrosoftIds ?? null,
      agentMicrosoftIds: agentMicrosoftIds ?? null,
      groupIds: groupIds ?? null,
      page,
      limit,
    }, { signal: controller.signal })
      .then(setData)
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setError(err.message || 'Failed to fetch data')
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [startDate, endDate, previousStartDate, previousEndDate, queueMicrosoftIds, agentMicrosoftIds, groupIds, page]);

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [startDate, endDate, queueMicrosoftIds, agentMicrosoftIds, groupIds]);

  // Extract and group status data by categories
  const { transformedData, activeStatusKeys, COLORS } = React.useMemo(() => {
    if (!data?.data || data.data.length === 0) {
      return { transformedData: [], activeStatusKeys: [], COLORS: {} };
    }

    // Generate colors for all possible grouped categories
    const colors = generateStatusColors(theme);

    // Transform data to group individual statuses into categories
    const transformed = [...data.data]
      .map(item => {
        const groupedData: { [key: string]: number } = {};
        let totalValue = 0;

        // Initialize all groups to 0
        Object.keys(STATUS_GROUPS).forEach(groupName => {
          groupedData[groupName] = 0;
        });

        // Aggregate individual status values into groups
        Object.entries(STATUS_GROUPS).forEach(([groupName, statusList]) => {
          statusList.forEach(statusKey => {
            // Direct access to the property since we know the exact API format
            if (item[statusKey] !== undefined && typeof item[statusKey] === 'number') {
              const value = (item[statusKey] as number) || 0;
              groupedData[groupName] += value;
              totalValue += value;
            }
          });
        });

        type InputItem = { agent?: string; agentName?: string } & { [key: string]: number | string | undefined };
        const input = item as unknown as InputItem;
        return {
          agent: input.agent || input.agentName || '',
          agentName: item.agentName,
          ...groupedData,
          total: totalValue
        };
      })
      .slice(0, agentLimit);

    // Find which groups have at least one value > 0
    const activeGroups = Object.keys(STATUS_GROUPS).filter(groupName =>
      transformed.some(item => (item[groupName] as number) > 0)
    );

    return {
      transformedData: transformed,
      activeStatusKeys: activeGroups,
      COLORS: colors
    };
  }, [data?.data, agentLimit, theme]);

  // Memoized calculations
  const { totalItems, totalPages, startIndex, endIndex, contentHeight } = React.useMemo(() => {
    const rowsOnPage = Math.min(limit, transformedData.length || 0);
    const heightRows = loading ? MAX_ROWS_FOR_CARD : (rowsOnPage || 1);
    const height = unifiedChartHeight ?? Math.max(300, Math.min(560, heightRows * 40 + 80));

    const items = data?.total || 0;
    const pages = Math.max(1, Math.ceil(items / limit));
    const startIdx = (page - 1) * limit + 1;
    const endIdx = Math.min(page * limit, items);

    return {
      totalItems: items,
      totalPages: pages,
      startIndex: startIdx,
      endIndex: endIdx,
      contentHeight: height
    };
  }, [limit, transformedData.length, loading, unifiedChartHeight, data?.total, page]);

  // Refs and state
  const chartRef = React.useRef<HTMLDivElement | null>(null);
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const [mouseTip, setMouseTip] = React.useState<{
    visible: boolean;
    x: number;
    y: number;
    content: React.ReactNode;
  }>({ visible: false, x: 0, y: 0, content: null });

  // Tooltip positioning utility
  const computeTooltipPosition = React.useCallback((clientX: number, clientY: number) => {
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = tooltipRef.current?.offsetWidth ?? 260;
    const height = tooltipRef.current?.offsetHeight ?? 120;

    let x = clientX + margin;
    let y = clientY + margin;

    if (x + width > vw - 8) {
      x = clientX - width - margin;
    }
    if (y + height > vh - 8) {
      y = Math.max(8, vh - height - 8);
    }

    return { x, y };
  }, []);

  // Pagination component
  const renderPagination = React.useCallback(() => (
    <div className="flex items-center justify-between w-full">
      <div className="text-xs text-muted-foreground">
        Showing {startIndex}-{endIndex} of {totalItems}
      </div>
      <div className="flex items-center gap-2">
        <button
          className="w-8 h-8 grid place-items-center rounded border disabled:opacity-50"
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1 || loading}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs px-1">{page} / {totalPages}</span>
        <button
          className="w-8 h-8 grid place-items-center rounded border disabled:opacity-50"
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages || loading}
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  ), [startIndex, endIndex, totalItems, page, totalPages, loading]);

  // Priority order for grouped status display
  const prioritizedOrder = React.useCallback((keys: string[]) => {
    const priority = ['Available', 'In a call', 'Not available', 'Busy'];
    return [...keys].sort((a, b) => {
      const ia = priority.indexOf(a);
      const ib = priority.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return a.localeCompare(b);
    });
  }, []);

  // Chart content rendering
  const chartContent = React.useMemo(() => {
    // Loading state
    if (loading) {
      return (
        <div className="p-3 pb-0 pointer-events-none" style={{ height: contentHeight }}>
          <div className="space-y-2">
            {Array.from({ length: MAX_ROWS_FOR_CARD }).map((_, idx) => (
              <div key={idx} className="flex items-center py-1.5">
                <div className="w-32 pr-4 flex-shrink-0">
                  <Skeleton className="h-3 w-24" />
                </div>
                <div className="flex-1">
                  <div className="h-6 rounded bg-gray-200 dark:bg-white/10 overflow-hidden">
                    <div
                      className="h-full animate-pulse bg-gray-300 dark:bg-white/20"
                      style={{ width: `${40 + (idx % 4) * 10}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // No data state
    if (!data?.data || data.data.length === 0) {
      return (
        <div className="p-3 pb-0 flex items-center justify-center h-[300px] text-sm text-muted-foreground dark:text-white/60">
          <div className="flex flex-col items-center gap-2">
            <span>No call records were found</span>
          </div>
        </div>
      );
    }

    // Data rendering
    const start = (page - 1) * limit;
    const end = start + limit;
    const pageData = data?.total && data.total > transformedData.length
      ? transformedData
      : transformedData.slice(start, end);

    return (
      <div ref={chartRef} className="p-3">
        <div className="p-3 flex flex-wrap items-center justify-center gap-2 text-center">
          {prioritizedOrder(activeStatusKeys).filter(k => k !== 'noData').map((k) => (
            <div key={k} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ background: COLORS[k] }} />
              <span className="text-xs">{formatStatusName(k)}</span>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          {pageData.map((row: Record<string, number | string>, idx: number) => {
            const totalValue = Number((row['total'] as number | string) || 0);
            const orderedKeys = prioritizedOrder(activeStatusKeys);

            return (
              <AgentBarRow
                key={`${String(row['agent'])}-${idx}`}
                name={String(row['agent'])}
                right={
                  <div
                    className={`flex h-6 bg-gray-200 rounded overflow-hidden ${orderedKeys.some((k) => Number((row[k] as number) || 0) > 0)
                      ? 'cursor-pointer'
                      : 'cursor-default'
                      }`}
                    onMouseEnter={(e) => {
                      const nonZeroKeys = orderedKeys.filter((k) => Number((row[k] as number) || 0) > 0);
                      if (nonZeroKeys.length === 0) return;
                      const content = (
                        <div className="text-xs">
                          {nonZeroKeys.map((k) => {
                            const value = Number((row[k] as number) || 0);
                            return (
                              <div key={`tip-${k}`} className="flex items-center gap-2 py-0.5">
                                <span className="w-2 h-2 rounded" style={{ background: COLORS[k] }} />
                                <span className="min-w-[120px]" style={{ color: COLORS[k] }}>
                                  {formatStatusName(k)}:
                                </span>
                                <span className="font-medium">{formatSeconds(value)}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                      const pos = computeTooltipPosition(e.clientX, e.clientY);
                      setMouseTip({ visible: true, x: pos.x, y: pos.y, content });
                    }}
                    onMouseMove={(e) => {
                      if (!mouseTip.visible) return;
                      const pos = computeTooltipPosition(e.clientX, e.clientY);
                      setMouseTip((prev) => ({ ...prev, x: pos.x, y: pos.y }));
                    }}
                    onMouseLeave={() => setMouseTip({ visible: false, x: 0, y: 0, content: null })}
                  >
                    {orderedKeys.map((k) => {
                      const value = Number((row[k] as number) || 0);
                      if (!value || totalValue === 0) return null;
                      const width = Math.max(0, Math.min(100, (value / totalValue) * 100));
                      return (
                        <div
                          key={k}
                          className="h-full flex items-center justify-center text-white text-[10px] md:text-[10px] font-medium"
                          style={{ width: `${width}%`, background: COLORS[k] }}
                        >
                          {width > 15 && formatSeconds(value)}
                        </div>
                      );
                    })}
                  </div>
                }
              />
            );
          })}
        </div>
        {mouseTip.visible && (
          <div
            ref={tooltipRef}
            className="fixed z-50 pointer-events-none border rounded-md shadow-md p-2 text-xs bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            style={{ left: mouseTip.x, top: mouseTip.y }}
          >
            {mouseTip.content}
          </div>
        )}
      </div>
    );
  }, [
    loading,
    data,
    contentHeight,
    transformedData,
    page,
    limit,
    activeStatusKeys,
    COLORS,
    prioritizedOrder,
    computeTooltipPosition,
    mouseTip
  ]);

  return (
    <Card className="w-full overflow-hidden h-full bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] flex flex-col">
      <CardHeader className="dark:border-b flex-shrink-0">
        <div className="flex items-center gap-2">
          <span>Status Over Time</span>
          <InfoTooltip
            content="The duration that the agents have been staying in each status within a given time."
          />
        </div>
      </CardHeader>
      <CardContent className="p-0 bg-white dark:bg-[#0C3451] dark:text-white border-t dark:border-[#353535] flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-auto">
          {chartContent}
        </div>
        {!loading && data?.data && data.data.length > 0 && (
          <div className="border-t p-3 flex-shrink-0 dark:border-white">
            {renderPagination()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StatusOverTimeWidget;
